import React, { useMemo, useState, type FC, type CSSProperties } from 'react'
import type { IconBaseProps } from 'react-icons'
import { MdKeyboardArrowLeft as MdKeyboardArrowLeftRaw } from 'react-icons/md'
import { notification } from 'antd'
import { useTranslation } from 'react-i18next'
import './CreateOrderPlaylistView.scss'
import SearchInput from '../SearchInput/SearchInput'

import { useAppDispatch, useAppSelector } from '../../utils/redux/store'
import {
  attemptGetUserOrders,
  attemptOrderPlayList,
  OrderPlaylistDto,
} from '../../utils/redux/ordersSlice'

/** FIX TS2786 */
const MdKeyboardArrowLeft = MdKeyboardArrowLeftRaw as unknown as FC<IconBaseProps>

type ClientsKey = 'male' | 'age18_30' | 'age31_45' | 'age46_60'

type StyleKey =
  | 'pop'
  | 'jazzBlues'
  | 'classical'
  | 'rock'
  | 'hiphopRnb'
  | 'retro'
  | 'clubDeepHouse'
  | 'funkSoul'
  | 'loungeChillout'
  | 'bossaNova'
  | 'latina'
  | 'italian'
  | 'france'
  | 'ukrainian'
  | 'georgian'

function CreateOrderPlaylistView({ onBack }: { onBack: () => void }) {
  const { t, i18n } = useTranslation('orders')
  const dispatch = useAppDispatch()

  // если у тебя есть auth.user.name — можно автозаполнить LOGIN
  const userName = useAppSelector((s: any) => s.auth?.user?.name) ?? ''

  const [form, setForm] = useState({
    login: userName || '',
    activity: '',
    address: '',
  })

  // По ТЗ: все слайдеры default "off" (ставим 0)
  const [clients, setClients] = useState<Record<ClientsKey, number>>({
    male: 0,
    age18_30: 0,
    age31_45: 0,
    age46_60: 0,
  })

  const stylesList = useMemo<Array<{ key: StyleKey; label: string }>>(
    () => [
      { key: 'pop', label: t('create.styles.options.pop') },
      { key: 'jazzBlues', label: t('create.styles.options.jazzBlues') },
      { key: 'classical', label: t('create.styles.options.classical') },
      { key: 'rock', label: t('create.styles.options.rock') },
      { key: 'hiphopRnb', label: t('create.styles.options.hiphopRnb') },
      { key: 'retro', label: t('create.styles.options.retro') },
      { key: 'clubDeepHouse', label: t('create.styles.options.clubDeepHouse') },
      { key: 'funkSoul', label: t('create.styles.options.funkSoul') },
      { key: 'loungeChillout', label: t('create.styles.options.loungeChillout') },
      { key: 'bossaNova', label: t('create.styles.options.bossaNova') },
      { key: 'latina', label: t('create.styles.options.latina') },
      { key: 'italian', label: t('create.styles.options.italian') },
      { key: 'france', label: t('create.styles.options.france') },
      { key: 'ukrainian', label: t('create.styles.options.ukrainian') },
      { key: 'georgian', label: t('create.styles.options.georgian') },
    ],
    [i18n.language, t],
  )

  // По ТЗ: все чекбоксы/тумблеры default OFF
  const [styles, setStyles] = useState<Record<StyleKey, boolean>>({
    pop: false,
    jazzBlues: false,
    classical: false,
    rock: false,
    hiphopRnb: false,
    retro: false,
    clubDeepHouse: false,
    funkSoul: false,
    loungeChillout: false,
    bossaNova: false,
    latina: false,
    italian: false,
    france: false,
    ukrainian: false,
    georgian: false,
  })

  const sliders = useMemo<Array<{ key: ClientsKey; label: string }>>(
    () => [
      { key: 'male', label: t('create.clients.male') },
      { key: 'age18_30', label: t('create.clients.age18_30') },
      { key: 'age31_45', label: t('create.clients.age31_45') },
      { key: 'age46_60', label: t('create.clients.age46_60') },
    ],
    [i18n.language, t],
  )

  // По ТЗ: эти тоже default OFF
  const [ratios, setRatios] = useState({
    vocalInstrumental: 0,
    internationalUkrainian: 0,
  })

  const [trackLinks, setTrackLinks] = useState('')
  const [commentary, setCommentary] = useState('')
  const [consent, setConsent] = useState(false)
  const [submitting, setSubmitting] = useState(false)

  const hasSelectedStyles = useMemo(() => Object.values(styles).some(Boolean), [styles])

  const toggleStyle = (key: StyleKey) => {
    setStyles((p) => ({ ...p, [key]: !p[key] }))
  }

  const buildPayload = (): OrderPlaylistDto => {
    const males = clients.male
    const females = 100 - males

    const selectedStyles = (Object.keys(styles) as StyleKey[]).filter((k) => styles[k])

    return {
      businessName: form.login.trim(),
      activityType: form.activity.trim(),
      address: form.address.trim(),

      males: String(males),
      females: String(females),

      age18_30: String(clients.age18_30),
      age31_45: String(clients.age31_45),
      age46_60: String(clients.age46_60),

      vocalToInstrumental: String(ratios.vocalInstrumental),
      internationalToLocal: String(ratios.internationalUkrainian),

      extraClarifications: commentary.trim(),
      linkTracksExamples: trackLinks.trim(),

      // как mobile: отправляем ключи жанров
      styles: selectedStyles,
    }
  }

  const validate = (payload: OrderPlaylistDto) => {
    if (!payload.businessName) return t('create.errors.loginRequired')
    if (!payload.activityType) return t('create.errors.activityRequired')
    if (!payload.address) return t('create.errors.addressRequired')
    if (!payload.styles?.length) return t('create.errors.selectAtLeastOneStyle')
    if (!consent) return t('create.errors.consentRequired')
    return null
  }

  const onOrder = async () => {
    const payload = buildPayload()
    const err = validate(payload)

    if (err) {
      notification.error({
        title: 'Error',
        message: t('notifications.title'),
        description: err,
        placement: 'topRight',
      })
      return
    }

    try {
      setSubmitting(true)
      await dispatch(attemptOrderPlayList(payload)).unwrap()

      notification.success({
        title: 'Success',        
        message: t('notifications.title'),
        description: t('notifications.createdSuccessfully'),
        placement: 'topRight',
      })

      dispatch(attemptGetUserOrders())
      onBack()
    } catch (e: any) {
      notification.error({
        title: 'Error',        
        message: t('notifications.title'),
        description: e?.message || t('notifications.createFailed'),
        placement: 'topRight',
      })
    } finally {
      setSubmitting(false)
    }
  }

  const pageStyle = {
    ['--player-offset' as any]:
      'max(var(--app-bottom-overlay, 0px), var(--ga-desktop-offset, 0px))',
    paddingBottom: 'calc(var(--player-offset) + env(safe-area-inset-bottom) + 16px)',
  } as CSSProperties

  return (
    <div className="createOrderPage" style={pageStyle}>
      <div className="createOrderInner">
        <SearchInput placeholder={t('searchPlaceholder')} />

        <div style={{ marginTop: '100px' }} className="createOrderHeader">
          <button
            type="button"
            className="createOrderBackBtn"
            onClick={onBack}
            aria-label={t('back')}
          >
            <MdKeyboardArrowLeft size={50} />
          </button>

          <div className="createOrderHeaderTitle">{t('create.header')}</div>
        </div>

        <div className="createOrderSubtitle">{t('create.businessInfo')}</div>

        <div className="createOrderForm">
          <div className="createOrderField">
            <div className="createOrderLabel">{t('create.fields.login')}</div>
            <input
              className="createOrderInput"
              placeholder={t('create.placeholders.login')}
              value={form.login}
              onChange={(e) => setForm((p) => ({ ...p, login: e.target.value }))}
            />
          </div>

          <div className="createOrderField">
            <div className="createOrderLabel">{t('create.fields.activity')}</div>
            <input
              className="createOrderInput"
              placeholder={t('create.placeholders.activity')}
              value={form.activity}
              onChange={(e) => setForm((p) => ({ ...p, activity: e.target.value }))}
            />
          </div>

          <div className="createOrderField">
            <div className="createOrderLabel">{t('create.fields.address')}</div>
            <input
              className="createOrderInput"
              placeholder={t('create.placeholders.address')}
              value={form.address}
              onChange={(e) => setForm((p) => ({ ...p, address: e.target.value }))}
            />
          </div>
        </div>

        <div className="createOrderClientsTitle">{t('create.clientsInfo')}</div>

        <div className="clientsInfo">
          {sliders.map((s) => {
            const v = clients[s.key]
            return (
              <div key={s.key} className="clientRow">
                <div className="clientRowLabel">{s.label}</div>

                <div className="clientRowValue" aria-hidden="true">
                  {v}%
                </div>

                <div className="clientRowMid">
                  <div className="clientSideLabel">{t('create.clients.man')}</div>

                  <input
                    className="clientRange"
                    type="range"
                    min={0}
                    max={100}
                    value={v}
                    onChange={(e) =>
                      setClients((p) => ({ ...p, [s.key]: Number(e.target.value) }))
                    }
                    style={{ ['--p' as any]: `${v}%` }}
                    aria-label={s.label}
                  />

                  <div className="clientSideLabel clientSideLabelRight">
                    {t('create.clients.woman')}
                  </div>
                </div>
              </div>
            )
          })}
        </div>

        <div className="stylesBlock">
          <div className="stylesTitle">{t('create.styles.title')}</div>

          <div className="stylesList">
            {stylesList.map((item) => {
              const isOn = styles[item.key]
              return (
                <div key={item.key} className="styleRow">
                  <div className="styleLabel">{item.label}</div>

                  <button
                    type="button"
                    className={`styleToggle ${isOn ? 'isOn' : ''}`}
                    onClick={() => toggleStyle(item.key)}
                    role="switch"
                    aria-checked={isOn}
                    aria-label={item.label}
                  >
                    <span className="styleToggleThumb" />
                  </button>
                </div>
              )
            })}
          </div>
        </div>
      </div>

      <div className="extraBlock">
        <div className="ratioRow">
          <div className="ratioRowLabel">{t('create.ratios.vocalInstrumental')}</div>

          <div className="ratioRowValue" aria-hidden="true">
            {ratios.vocalInstrumental}%
          </div>

          <div className="ratioRowMid">
            <div className="ratioSideLabel">{t('create.ratios.vocal')}</div>

            <input
              className="ratioRange"
              type="range"
              min={0}
              max={100}
              value={ratios.vocalInstrumental}
              onChange={(e) =>
                setRatios((p) => ({ ...p, vocalInstrumental: Number(e.target.value) }))
              }
              style={{ ['--p' as any]: `${ratios.vocalInstrumental}%` }}
              aria-label={t('create.ratios.vocalInstrumental')}
            />

            <div className="ratioSideLabel ratioSideLabelRight">
              {t('create.ratios.instrumental')}
            </div>
          </div>
        </div>

        <div className="ratioRow">
          <div className="ratioRowLabel">{t('create.ratios.internationalLocal')}</div>

          <div className="ratioRowValue" aria-hidden="true">
            {ratios.internationalUkrainian}%
          </div>

          <div className="ratioRowMid">
            <div className="ratioSideLabel">{t('create.ratios.international')}</div>

            <input
              className="ratioRange"
              type="range"
              min={0}
              max={100}
              value={ratios.internationalUkrainian}
              onChange={(e) =>
                setRatios((p) => ({
                  ...p,
                  internationalUkrainian: Number(e.target.value),
                }))
              }
              style={{ ['--p' as any]: `${ratios.internationalUkrainian}%` }}
              aria-label={t('create.ratios.internationalLocal')}
            />

            <div className="ratioSideLabel ratioSideLabelRight">
              {t('create.ratios.local')}
            </div>
          </div>
        </div>

        <div className="extraField">
          <div className="extraFieldLabel">{t('create.linksLabel')}</div>
          <input
            className="extraFieldInput"
            placeholder={t('create.linksPlaceholder')}
            value={trackLinks}
            onChange={(e) => setTrackLinks(e.target.value)}
          />
        </div>

        <div className="commentaryBlock">
          <div className="commentaryTitle">{t('create.commentaryTitle')}</div>

          <textarea
            className="commentaryTextarea"
            placeholder={t('create.commentaryPlaceholder')}
            value={commentary}
            onChange={(e) => setCommentary(e.target.value)}
          />
        </div>

        <label className="consentRow">
          <input
            className="consentCheckbox"
            type="checkbox"
            checked={consent}
            onChange={(e) => setConsent(e.target.checked)}
          />
          <span className="consentText">{t('create.consent')}</span>
        </label>

        {/* Кнопка всегда над нижним overlay */}
        <div className="orderBtnDock">
          <button
            type="button"
            className="orderBtn"
            disabled={!consent || submitting || !hasSelectedStyles}
            onClick={onOrder}
          >
            {submitting ? t('create.submitting') : t('create.submit')}
          </button>
        </div>
      </div>
    </div>
  )
}

export default CreateOrderPlaylistView
